import { useState } from "react";
import { LoaderCircle, RefreshCw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { Button } from "@components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@components/ui/card";
import { Switch } from "@components/ui/switch";
import { Badge } from "@components/ui/badge";
import { ConfirmDialog } from "@components/ui/confirm-dialog";
import { deleteIntegrationConnection, testIntegrationConnection, updateIntegrationConnection, type IntegrationConnection } from "../../lib/integrations";
import { connectionSummary, setupCopy, syncProviders } from "./integrationPresentation";
import { IntegrationSyncControl } from "./IntegrationSyncControl";

export function ConnectedIntegrationCard({
  connection,
  onEdit,
  onReload,
  onOpenObligations,
}: {
  connection: IntegrationConnection;
  onEdit: (connection: IntegrationConnection) => void;
  onReload: () => Promise<void>;
  onOpenObligations: () => void;
}) {
  const [confirmingRemove, setConfirmingRemove] = useState(false);
  const testing = useAsyncAction();
  const managing = useAsyncAction();
  const [activeManagement, setActiveManagement] = useState<"default" | "enabled" | "remove" | null>(null);
  const testConnection = () => testing.run(async () => {
    try {
      await testIntegrationConnection(connection.id);
      toast.success("Connection successful.");
    } catch (error) { toast.error(error instanceof Error ? error.message : "Connection failed."); }
  });
  const manage = (kind: Exclude<typeof activeManagement, null>, operation: () => Promise<void>) => managing.run(async () => {
    setActiveManagement(kind);
    try { await operation(); await onReload(); }
    catch (error) {
      toast.error(error instanceof Error ? error.message : "Unable to update the integration.");
      if (kind === "remove") throw error;
    }
    finally { setActiveManagement(null); }
  });
  const makeDefault = () => manage("default", async () => {
    await updateIntegrationConnection(connection.id, { isDefault: true });
    toast.success(`${connection.name} is now the automatic delivery default.`);
  });
  const toggleEnabled = (enabled: boolean) => manage("enabled", async () => {
    await updateIntegrationConnection(connection.id, { status: enabled ? "active" : "disabled" });
  });
  const remove = () => manage("remove", async () => {
    await deleteIntegrationConnection(connection.id);
    toast.success("Integration removed.");
  });

  return (
    <>
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>{connection.name}</CardTitle>
            <CardDescription>{connectionSummary(connection)}</CardDescription>
          </div>
          <div className="flex gap-2">
            <Badge variant={connection.status === "active" ? "default" : "secondary"}>
              {connection.status}
            </Badge>
            {connection.isDefault ? <Badge variant="outline">Default</Badge> : null}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        <Button variant="outline" onClick={() => onEdit(connection)}>Configure</Button>
        <Button variant="outline" onClick={() => void testConnection()} loading={testing.pending} loadingLabel="Testing…">
          <RefreshCw className="mr-2 h-4 w-4" />Test
        </Button>
        {syncProviders.has(connection.provider) ? (
          <IntegrationSyncControl
            connectionId={connection.id}
            providerName={setupCopy[connection.provider].name}
            onOpenObligations={onOpenObligations}
          />
        ) : null}
        {connection.provider === "azure_devops" && !connection.isDefault ? (
          <Button variant="outline" onClick={() => void makeDefault()} disabled={managing.pending} loading={managing.pending && activeManagement === "default"} loadingLabel="Setting default…">Make default</Button>
        ) : null}
        <div className="flex min-h-11 items-center gap-2 px-2">
          <Switch
            aria-label={`${connection.name} enabled`}
            disabled={managing.pending}
            aria-busy={managing.pending && activeManagement === "enabled"}
            checked={connection.status === "active"}
            onCheckedChange={(checked) => void toggleEnabled(checked)}
          />
          <span className="text-sm" role="status">{managing.pending && activeManagement === "enabled" ? <><LoaderCircle className="mr-1 inline h-4 w-4 animate-spin motion-reduce:animate-none" aria-hidden="true" />Updating…</> : "Enabled"}</span>
        </div>
        <Button variant="outline" aria-label={`Remove ${connection.name}`} disabled={managing.pending} onClick={() => setConfirmingRemove(true)}>
          <Trash2 className="mr-2 h-4 w-4" />Remove
        </Button>
      </CardContent>
    </Card>
    <ConfirmDialog
      open={confirmingRemove}
      onOpenChange={setConfirmingRemove}
      title={`Remove ${connection.name}?`}
      description="Existing external work items will not be deleted."
      confirmLabel="Remove"
      destructive
      pending={managing.pending && activeManagement === "remove"}
      onConfirm={remove}
    />
    </>
  );
}

